import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { connect } from 'react-redux';
import {
  convertTokenToLowestUnit,
  convertTokenFromLowestUnit,
  findTokenByAddress,
} from '@blockcatio/tabby-library/lib/utilities/tokenUtilities';
import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';

import NavigateAwayPrompt from '../../components/NavigateAwayPrompt';
import Configuration from './Configuration';
import { createCampaignConfigurationSubmit } from '../../actions/createCampaign';
import {
  tokenAddressChanged,
  addCustomToken,
  fetchTokenInfo,
  validateTokenAddress,
} from '../../actions/token';
import { getTokenListByName } from '../../selectors/token';

const mapStateToProps = (state) => {
  const tokenList = getTokenListByName(state);
  const { name, tokensPerCode, tokenAddress } = state.createCampaign;
  return {
    name,
    tokenAddress,
    // If tokensPerCode is set, convert it from tokenWei
    tokensPerCode:
      tokensPerCode && tokenAddress
        ? convertTokenFromLowestUnit(tokenList, tokensPerCode, tokenAddress)
        : '',
    numberOfCodes: state.createCampaign.numberOfCodes,
    tokenList,
    abandonCampaignCreationMessage: state.app.abandonCampaignCreationMessage,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onCampaignCreate: (input) => {
    dispatch(createCampaignConfigurationSubmit(input));
    ownProps.onComplete();
  },
  onTokenAddressChange: tokenAddress => dispatch(tokenAddressChanged(tokenAddress)),
  onAddCustomToken: (...args) => dispatch(addCustomToken(...args)),
  onFetchTokenInfo: tokenAddress => dispatch(fetchTokenInfo(tokenAddress)),
  onValidateTokenAddress: tokenAddress => dispatch(validateTokenAddress(tokenAddress)),
});

export class ConfigurationContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      tokenAddress: '',
      hasData:
        props.name !== '' ||
        props.tokenAddress !== '' ||
        props.tokensPerCode !== '' ||
        props.numberOfCodes !== 0,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleCustomTokenFormSubmit = this.handleCustomTokenFormSubmit.bind(this);
    this.generateTokenOptions = this.generateTokenOptions.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleChange(input) {
    const {
      name, tokenAddress, tokensPerCode, numberOfCodes,
    } = input;

    const isChangingToken = tokenAddress !== this.state.tokenAddress && tokenAddress;
    // Values can be strings or null - use !! to cover !== null && !== ''
    const hasData = !!name || !!tokenAddress || !!tokensPerCode || !!numberOfCodes;
    // Only update & call if it changed.
    if (isChangingToken) {
      if (this.mounted) {
        this.setState({ tokenAddress });
      }
      // Update token address in store state - kick off balance/allowance fetches.
      this.props.onTokenAddressChange(tokenAddress);
    }
    if (this.mounted) {
      this.setState({ hasData });
    }
  }

  handleCustomTokenFormSubmit(tokenData) {
    this.props.onAddCustomToken(tokenData);
    return tokenData.address;
  }

  generateTokenOptions() {
    return this.props.tokenList.map((token) => {
      const { name, symbol, address } = token;
      return {
        value: address,
        label: `${name} (${symbol})`,
      };
    });
  }

  async handleSubmit(input) {
    let { tokenList } = this.props;
    const { onFetchTokenInfo, onCampaignCreate } = this.props;
    if (!findTokenByAddress(tokenList, input.tokenAddress)) {
      const tokenInfo = await onFetchTokenInfo(input.tokenAddress);
      tokenList = [...tokenList, tokenInfo];
    }
    const tokensPerCode = convertTokenToLowestUnit(
      tokenList,
      input.tokensPerCode,
      input.tokenAddress,
    );
    const totalTokens = tokensPerCode.mul(safeToBn(input.numberOfCodes));
    return onCampaignCreate({ ...input, tokensPerCode, totalTokens });
  }

  render() {
    const { abandonCampaignCreationMessage } = this.props;
    const { hasData } = this.state;
    const tokenOptions = this.generateTokenOptions();
    return (
      <React.Fragment>
        <NavigateAwayPrompt when={hasData} message={abandonCampaignCreationMessage} />
        <Configuration
          {...this.props}
          onChange={this.handleChange}
          onSelectFormSubmit={this.handleCustomTokenFormSubmit}
          onSubmit={this.handleSubmit}
          tokenOptions={tokenOptions}
        />
      </React.Fragment>
    );
  }
}

ConfigurationContainer.defaultProps = {
  name: '',
  tokenAddress: '',
  tokensPerCode: '',
  numberOfCodes: 0,
  onTokenAddressChange: noop,
  onAddCustomToken: noop,
  tokenList: null,
  abandonCampaignCreationMessage: '',
};

ConfigurationContainer.propTypes = {
  name: PropTypes.string,
  tokenAddress: PropTypes.string,
  tokensPerCode: PropTypes.string,
  numberOfCodes: PropTypes.number,
  onTokenAddressChange: PropTypes.func,
  onAddCustomToken: PropTypes.func,
  tokenList: PropTypes.arrayOf(PropTypes.shape({})),
  abandonCampaignCreationMessage: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigurationContainer);



// WEBPACK FOOTER //
// ./src/containers/createCampaign/ConfigurationContainer.js